'use client';

/**
 * usePlanRooms — the ONE fetch behind the rooms grid and its narrative.
 * Same discipline as usePlanLenses: strategy resolved through the
 * capability store, one deduped BFF call, everything arrives computed.
 *
 * @see klyde_edge/klyde_intelligence/projects/plan_rooms.py
 *
 * @component_role: Hook
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import type { CandidateOption, PriceRange } from './use-plan-lenses';

export interface RoomLine {
  requirement: string | null;
  requirement_id: string;
  quantity_in_room: number | null;
  also_serves: string[];
  state: 'selected' | 'open';
  quoted: boolean;
  pick: CandidateOption | null;
  candidate_count: number;
  unclassified_candidates: number;
  unit_price_range: PriceRange | null;
  tier_cells: Record<string, { mark: string; span: string }>;
}

export interface RoomPivot {
  room: string;
  real_room_count: number | null;
  one_room_at_tier: Record<string, number | null>;
  candidate_pool_by_tier: Record<string, number>;
  line_count: number;
  selected_count: number;
  open_count: number;
  lines: RoomLine[];
}

export interface PlanRoomsData {
  rooms: {
    pattern: string;
    data: {
      requirement_count: number;
      selected_requirement_count: number;
      open_requirement_count: number;
      requirement_in_room_lines: number;
      tier_columns: string[];
      rooms: RoomPivot[];
    };
  };
  labels: Record<string, string>;
}

export function usePlanRooms() {
  const { mcpId } = useViewportSelection();
  const strategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'plan_rooms'));
  const { data, isLoading } = useStrategyData<PlanRoomsData>(
    strategy, { semantic_id: mcpId ?? '' }, !!mcpId && !!strategy,
  );
  const queryKey = strategy ? ['strategy-data', strategy, { semantic_id: mcpId ?? '' }] : null;
  return { mcpId, strategy, d: data?.data, isLoading, queryKey };
}
